export type GameId = 'craps';
export type TableId = `${GameId}:${string}`;

export interface PersistedTable<TRules = unknown> {
  /** Game-specific rules — see each engine's *Rules type. */
  rules: TRules;
  /** Per-table preferences (selected chip denomination, etc.). */
  prefs: TablePrefs;
  /** True if the table is reachable from the lobby. */
  unlocked: boolean;
  /**
   * True after the lobby has played its one-time velvet-rope-drop animation
   * for this tile. Independent of `unlocked` so the animation plays on the
   * NEXT lobby visit after unlock, not at the moment bankroll crosses.
   * Standard tables seed this true (no celebration needed for the floor).
   */
  unlockSeenInLobby: boolean;
  /** Millisecond timestamp when `unlocked` first flipped to true; null if never. */
  unlockedAt: number | null;
}

export interface TablePrefs {
  selectedDenomCents: number;
}

export interface PersistedGame {
  tables: Record<string, PersistedTable>;
}

/**
 * The single in-flight game. Non-null iff the player is seated at a table.
 * Replaces the old per-table `session` blob — only one game can be active at
 * a time. `snapshot` is the engine's serialized state (typed `unknown` here
 * because core cannot import a game engine; the game package casts it to
 * CrapsSnapshot). While activeGame is set, `snapshot.bankroll` is the live
 * cash-in-hand and `PersistedV2.bankrollCents` is the frozen floor wallet.
 */
export interface ActiveGame {
  tableId: TableId;
  /** Minted once per game; reused across reloads, new on Reset. */
  gameId: string;
  /** Per-game shooter-turn counter (1-based); increments on seven-out. */
  turnId: number;
  /** Highest event seq written for the current turn (0 = only the keyframe). */
  lastSeq: number;
  startedAt: number;
  snapshot: unknown;
}

export interface PersistedV2 {
  bankrollCents: number;
  activeGame: ActiveGame | null;
  lobby: { lastTableId: TableId };
  games: {
    craps: PersistedGame;
  };
}

export const PERSISTENCE_KEY_V2 = 'casino.v2';
export const PERSISTENCE_KEY_V1 = 'casino.v1';
